// Der Sammel-Bildschirm, Projektplan 7.2 (IMG_4893 bis IMG_4923, 4925,
// 4926, 01.09.2026). Eingabe, Treffer, Stern, Bläattern, die zwei Ringe.
//
// Die Suche laeuft ab Werk global ueber alle sichtbaren Laender
// gleichzeitig: Beim Durchblaettern eines Codes wechseln Flagge UND
// Fortschritts-Ring live mit jedem Treffer (siehe lib/treffer.ts).
//
// Die Seitenleiste steuert das (Nutzerwunsch 10.09.2026, E77): ein
// angetipptes Land beschraenkt die Suche auf genau dieses Land, der
// Eintrag "Laenderkennzeichen" darueber holt die globale Suche zurueck.
// landWaehlen() legt dafuer den Schalter "Globale Suche" um.

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Flag, Map as MapIcon, Menu, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Drawer } from "../bausteine/Drawer.js";
import { KennzeichenGrafik } from "../bausteine/KennzeichenGrafik.js";
import { Ring } from "../bausteine/Ring.js";
import { useBundle, type Land } from "../lib/bundle.js";
import {
  persoenlicheSammlung,
  punkteGesamt,
  useEintragDetails,
  useEintragEntfernen,
  useEintragSetzenMitSpiegel,
  useEntries,
  type Eintragskarte,
} from "../lib/entries.js";
import { aktiveSammlung } from "../lib/collections.js";
import { useEinstellungenSpeichern, type LandSichtbarkeit } from "../lib/einstellungen.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import {
  LAENDERKENNZEICHEN_MODUS,
  findeTreffer,
  fortschrittFuer,
  type Fortschritt,
  type Treffer,
} from "../lib/treffer.js";
import type { Kennzeichen } from "../lib/bundle.js";

// Stabile Referenzen fuer den Fallback, solange das Bundle laedt. Ein
// frisches [] bei jedem Rendern wuerde useMemo unten seinen Sinn nehmen.
const LEERE_LAENDER: Land[] = [];
const LEERE_KENNZEICHEN: Kennzeichen[] = [];

interface Settings {
  activeCountry?: string;
  countryOrder?: string[];
  countryVisibility?: Record<string, LandSichtbarkeit>;
  activeCollectionId?: number;
  /** Zusaetzlich zur aktiven (geteilten) Sammlung auch eigene sammeln. */
  spiegleInEigene?: boolean;
  punkteAusblenden?: boolean;
  historischeAusblenden?: boolean;
  diplomatenAnzeigen?: boolean;
  globaleSuche?: boolean;
  eingabeLoeschenBeiNeu?: boolean;
  eingabeLoeschenBeiVorhandenSekunden?: 0 | 1 | 2 | 3 | 4;
}

function beschriftung(treffer: Treffer): string {
  if (treffer.kennzeichen.regionNames.length > 0) return treffer.kennzeichen.regionNames.join(" / ");
  return treffer.kennzeichen.derivation ?? treffer.kennzeichen.code;
}

function gesehenAm(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Wie im Original: "Ca. 517.013 Fahrzeuge mit diesem Kennzeichen". */
function fahrzeugbestandText(anzahl: number): string {
  return `Ca. ${anzahl.toLocaleString("de-DE")} Fahrzeuge mit diesem Kennzeichen`;
}

/**
 * Beschriftung des Zusatzfeld-Blocks unter der Trefferzeile: Deutschland
 * zeigt zwei Spalten "Abgeleitet von:"/"Bundesland:" (derivation ist dort
 * die Herkunft des Buchstabens), Polen ebenfalls zwei Spalten
 * "Woiwodschaft:"/"Deutsch:" (derivation der polnische Eigenname,
 * stateName die Uebersetzung - docs/reference/polen-kr-krakow.png).
 * Irland zeigt dagegen nur EIN Feld, "Irisch:" mit dem gaelischen Namen
 * (derivation), keine zweite Spalte (docs/reference/irland-d-dublin.png) -
 * rechts ist dort deshalb null, was die zweite Spalte in Hauptscreen
 * entfallen laesst. Je Land eigens aus einem echten Screenshot
 * uebernommen, nicht geraten - ein viertes Land braucht seinen eigenen Beleg.
 * Frankreich zeigt das Spiegelbild von Irland: nur EIN Feld, rechts,
 * "Region:" mit der alten Region (docs/reference/frankreich-67.png,
 * stateName aus kennzeichen-fr.mjs) - links ist deshalb null.
 */
function zusatzfeldLabel(iso2: string): { links: string | null; rechts: string | null } {
  if (iso2 === "PL") return { links: "Woiwodschaft:", rechts: "Deutsch:" };
  if (iso2 === "IE") return { links: "Irisch:", rechts: null };
  if (iso2 === "FR") return { links: null, rechts: "Region:" };
  return { links: "Abgeleitet von:", rechts: "Bundesland:" };
}

export function Hauptscreen({ nutzer, sammlungen }: { nutzer: Nutzer; sammlungen: Sammlung[] }) {
  const [drawerOffen, setDrawerOffen] = useState(false);
  const [eingabe, setEingabe] = useState("");
  const [trefferIndex, setTrefferIndex] = useState(0);

  const bundle = useBundle();
  const settings = (nutzer.settings ?? {}) as Settings;
  const sammlung = aktiveSammlung(sammlungen, settings.activeCollectionId);
  const persoenliche = persoenlicheSammlung(sammlungen);
  const eintraege = useEntries(sammlung?.id);
  const eintragDetails = useEintragDetails(sammlung?.id);
  const eintragEntfernen = useEintragEntfernen(sammlung?.id);
  // Spiegeln (Einstellungen.tsx): in einer geteilten Sammlung kann jedes
  // Mitglied zusaetzlich in die eigene Sammlung eintragen, ohne dafuer
  // extra umzuschalten. Die Regel steckt im Hook, damit sie auf jedem
  // Eingabeweg gleich gilt (lib/entries.ts).
  const { setzen: eintragSetzen, spiegeln } = useEintragSetzenMitSpiegel(
    sammlung,
    sammlungen,
    settings.spiegleInEigene,
  );
  // Zweite Abfrage fuer die Ringe unten. Ist die persoenliche Sammlung
  // zugleich die aktive, teilen sich beide Aufrufe denselben Query-Key
  // und es geht keine Anfrage mehr raus.
  const persoenlicheEintraege = useEntries(persoenliche?.id);
  const einstellungenSpeichern = useEinstellungenSpeichern();

  const nutzerReihenfolge = useMemo(() => settings.countryOrder ?? [], [settings.countryOrder]);
  const sichtbarkeit = useMemo(() => settings.countryVisibility ?? {}, [settings.countryVisibility]);

  const laender = bundle.data?.laender ?? LEERE_LAENDER;
  const kennzeichen = bundle.data?.kennzeichen ?? LEERE_KENNZEICHEN;
  const karte = eintraege.data ?? new Map<number, number>();
  const persoenlicheKarte = persoenlicheEintraege.data ?? new Map<number, number>();

  // Fehlt activeCountry (erster Besuch), gilt das erste sichtbare Land.
  const aktivesLand = settings.activeCountry ?? laender[0]?.iso2 ?? "";

  // Einstellungen-Bildschirm, Phase 11: fehlt ein Schalter (kein Eintrag in
  // users.settings), gilt die im Referenz-Screenshot gezeigte Vorgabe
  // (siehe lib/einstellungen.ts) - nur "diplomatenAnzeigen" ist das positiv
  // formulierte Gegenstueck der beiden "...ausblenden"-Schalter.
  const historischeAusblenden = settings.historischeAusblenden ?? false;
  const diplomatenAnzeigen = settings.diplomatenAnzeigen ?? true;
  const globaleSuche = settings.globaleSuche ?? true;
  const punkteAusblenden = settings.punkteAusblenden ?? false;
  const eingabeLoeschenBeiNeu = settings.eingabeLoeschenBeiNeu ?? true;
  const eingabeLoeschenBeiVorhandenSekunden = settings.eingabeLoeschenBeiVorhandenSekunden ?? 0;

  const trefferFilter = useMemo(
    () => ({ historischeAusblenden, diplomatenAnzeigen, nurLand: globaleSuche ? undefined : aktivesLand }),
    [historischeAusblenden, diplomatenAnzeigen, globaleSuche, aktivesLand],
  );

  const treffer = useMemo(
    () => findeTreffer(eingabe, kennzeichen, laender, nutzerReihenfolge, sichtbarkeit, trefferFilter),
    [eingabe, kennzeichen, laender, nutzerReihenfolge, sichtbarkeit, trefferFilter],
  );

  // Springt der Treffer-Index durch eine neue Eingabe ins Leere, auf den
  // letzten gueltigen zurueckfallen statt auf einen leeren Bildschirm.
  useEffect(() => {
    if (trefferIndex >= treffer.length) setTrefferIndex(0);
  }, [treffer.length, trefferIndex]);

  const aktuellerTreffer = treffer[trefferIndex];

  // Nutzerwunsch 02.09.2026: Beide Eingabefeld-Loesch-Einstellungen leeren
  // nur das Eingabefeld (fuer den naechsten Code), nicht auch die Info zum
  // gerade behandelten Kennzeichen - die bleibt stehen, bis wirklich ein
  // neues Zeichen getippt wird (eingabeAendern loescht sie dann). Ohne
  // dieses "Geheftete" wuerde das Leeren von `eingabe` aktuellerTreffer und
  // damit den ganzen Infoblock sofort mitreissen.
  const [geheftetesTreffer, setGeheftetesTreffer] = useState<Treffer | undefined>(undefined);
  const anzeigeTreffer = aktuellerTreffer ?? geheftetesTreffer;

  const anzeigeLand: Land | undefined = anzeigeTreffer?.land ?? laender.find((l) => l.iso2 === aktivesLand);
  const gesammelt = anzeigeTreffer ? karte.has(anzeigeTreffer.kennzeichen.id) : false;

  // Einstellungen "Eingabefeld loeschen, wenn das Kennzeichen bereits
  // gesehen wurde" (docs/reference/einstellungen-sekunden-menue.png): erst
  // nach der gewaehlten Wartezeit, damit der Treffer noch kurz sichtbar
  // bleibt. Der Timer startet neu, sobald sich die Eingabe aendert - ein
  // frueherer Tastendruck darf keinen spaeteren Fund vorzeitig loeschen.
  useEffect(() => {
    if (!gesammelt || eingabeLoeschenBeiVorhandenSekunden === 0 || eingabe.length === 0) return;
    const timer = setTimeout(() => {
      setGeheftetesTreffer(aktuellerTreffer);
      setEingabe("");
      setTrefferIndex(0);
    }, eingabeLoeschenBeiVorhandenSekunden * 1000);
    return () => clearTimeout(timer);
  }, [gesammelt, eingabe, eingabeLoeschenBeiVorhandenSekunden, aktuellerTreffer]);
  const eigenerEintrag = anzeigeTreffer ? eintragDetails.get(anzeigeTreffer.kennzeichen.id) : undefined;

  // Laenderkennzeichen ("NL", "PL" ...) haengen in den Stammdaten an einem
  // Land, zaehlen laut E9 aber nur in ihre eigene Kategorie. Der Ring muss
  // deshalb dorthin zeigen und nicht auf das Gastland, sonst zeigte ein
  // Treffer im Modus "Laenderkennzeichen" ploetzlich Deutschlands Stand.
  const fortschrittBezug = anzeigeTreffer
    ? anzeigeTreffer.kennzeichen.scope === "country"
      ? LAENDERKENNZEICHEN_MODUS
      : anzeigeTreffer.land.iso2
    : aktivesLand;
  // Ein Block je Sammlung, in die gerade eingetragen wird. Sammelt man
  // nur die eigene, steht nur sie da; sammelt man nur die gemeinsame,
  // nur diese; spiegelt man, stehen beide untereinander.
  const gemeinsame = sammlung?.kind === "shared" ? sammlung : undefined;
  const zeigePersoenliche = !gemeinsame || spiegeln;
  const fortschritt = fortschrittFuer(fortschrittBezug, kennzeichen, laender, karte);

  function eingabeAendern(naechste: string) {
    setGeheftetesTreffer(undefined);
    setEingabe(naechste.toUpperCase());
    setTrefferIndex(0);
  }

  function sternTippen() {
    if (!anzeigeTreffer || !sammlung) return;
    if (gesammelt) {
      eintragEntfernen.mutate(anzeigeTreffer.kennzeichen.id);
    } else {
      const plateCodeId = anzeigeTreffer.kennzeichen.id;
      const punkte = anzeigeTreffer.kennzeichen.points;
      eintragSetzen({ plateCodeId, punkte });
      if (eingabeLoeschenBeiNeu) {
        setGeheftetesTreffer(anzeigeTreffer);
        setEingabe("");
        setTrefferIndex(0);
      }
    }
  }

  function landWaehlen(iso2OderModus: string) {
    // Die Wahl in der Seitenleiste stellt die Suche mit um. Ein Land
    // beschraenkt sie darauf: ohne das blieb ein Klick auf Irland
    // folgenlos, denn gesucht wurde weiter ueberall, und weil sortiert
    // wird wie die Laenderliste, stand bei jedem Code wieder Deutschland
    // vorn. "Laenderkennzeichen" macht sie umgekehrt wieder global - der
    // Eintrag ist damit der sichtbare Weg zurueck, den es sonst nur
    // versteckt in den Einstellungen gaebe.
    einstellungenSpeichern.mutate({
      activeCountry: iso2OderModus,
      globaleSuche: iso2OderModus === LAENDERKENNZEICHEN_MODUS,
    });
  }

  function reihenfolgeAendern(neu: string[]) {
    einstellungenSpeichern.mutate({ countryOrder: neu });
  }

  function sichtbarkeitAendern(iso2: string, naechste: LandSichtbarkeit) {
    einstellungenSpeichern.mutate({ countryVisibility: { ...sichtbarkeit, [iso2]: naechste } });
  }

  return (
    <div className="min-h-full bg-flaeche-grund font-sans text-neutral-900">
      <header className="flex items-center justify-between bg-akzent px-2 py-3 text-white shadow">
        <button
          type="button"
          onClick={() => setDrawerOffen(true)}
          aria-label="Menü"
          className="flex min-h-11 min-w-11 items-center justify-center"
        >
          <Menu size={24} />
        </button>
        <h1 className="text-lg font-medium">Kennzeichensammler</h1>
        <Link
          to={anzeigeTreffer ? `/karte?plateCodeId=${anzeigeTreffer.kennzeichen.id}` : "/karte"}
          aria-label="Karte"
          className="flex min-h-11 min-w-11 items-center justify-center"
        >
          <MapIcon size={22} />
        </Link>
      </header>

      <Drawer
        offen={drawerOffen}
        onClose={() => setDrawerOffen(false)}
        laender={laender}
        nutzerReihenfolge={nutzerReihenfolge}
        sichtbarkeit={sichtbarkeit}
        aktivesLand={aktivesLand}
        onLandWaehlen={landWaehlen}
        onReihenfolgeAendern={reihenfolgeAendern}
        onSichtbarkeitAendern={sichtbarkeitAendern}
      />

      <main className="mx-auto flex max-w-md flex-col items-center gap-4 p-4 pt-8">
        <div className="flex w-full items-center justify-center gap-6">
          {/* Nur EIN Pfeil, nicht vor UND zurueck (Referenz-Screenshots
              01.09.2026, Suche "B"): er blaettert immer vorwaerts durch die
              Laenderreihenfolge und laeuft nach dem letzten Treffer wieder
              bei Deutschland (Rang 0) an - deshalb (i + 1) trotz des nach
              links zeigenden Pfeils, keine (i - 1)-Logik mehr. */}
          {treffer.length > 1 && (
            <button
              type="button"
              onClick={() => setTrefferIndex((i) => (i + 1) % treffer.length)}
              aria-label="Nächster Treffer"
              className="flex min-h-11 min-w-11 items-center justify-center text-neutral-400"
            >
              <ChevronLeft size={26} />
            </button>
          )}
          <KennzeichenGrafik land={anzeigeLand} wert={eingabe} onWertAendern={eingabeAendern} />
          {/* Kein disabled/opacity-30 mehr (IMG_4893): der Stern bleibt auch
              ohne Treffer volltoenig schwarz, sternTippen() prueft
              anzeigeTreffer selbst und tut ohne Treffer einfach nichts. */}
          <button
            type="button"
            onClick={sternTippen}
            aria-label={gesammelt ? "Aus der Sammlung entfernen" : "Zur Sammlung hinzufügen"}
            className="flex min-h-11 min-w-11 items-center justify-center text-black"
          >
            <Star size={38} fill={gesammelt ? "#FDD835" : "none"} />
          </button>
        </div>

        <div className="min-h-24 w-full text-center">
          {anzeigeTreffer && (
            <>
              <p className="text-lg">{beschriftung(anzeigeTreffer)}</p>
              {/* Referenz-Screenshot 02.09.2026 (docs/reference/
                  diplomatenkennzeichen-hinweis-0120-schweiz.png): Code
                  "0120" zeigt "Schweiz" mit dem kursiven Zusatz
                  "(Diplomatenkennzeichen)" darunter - ohne den Hinweis
                  liesse sich ein Diplomatencode nicht von einem echten
                  Laendernamen unterscheiden. */}
              {anzeigeTreffer.kennzeichen.scope === "diplomatic" && (
                <p className="text-sm italic text-neutral-500">(Diplomatenkennzeichen)</p>
              )}
              {anzeigeTreffer.kennzeichen.vehicleCountApprox !== null && (
                <p className="text-sm italic text-neutral-500">
                  {fahrzeugbestandText(anzeigeTreffer.kennzeichen.vehicleCountApprox)}
                </p>
              )}
              {anzeigeTreffer.kennzeichen.status !== "aktuell" && (
                <p className="text-sm italic text-neutral-500">
                  {anzeigeTreffer.kennzeichen.status === "historisch" ? "(historisch)" : "(nicht mehr ausgegeben)"}
                </p>
              )}
              {eigenerEintrag ? (
                // IMG_4894: nach dem Sammeln ersetzt "Gesehen am ..." die
                // reine Punktezahl, mit dem tatsaechlich vergebenen
                // pointsAwarded (nicht dem aktuellen plate_codes.points -
                // beide stimmen zwar hier ueberein, aber nur Ersteres ist
                // die Quelle der Wahrheit, siehe E8/E10).
                <p className="text-sm italic text-neutral-500">
                  Gesehen am {gesehenAm(eigenerEintrag.firstSeenAt)}
                  {!punkteAusblenden && ` - ${eigenerEintrag.pointsAwarded} Punkte`}
                </p>
              ) : (
                !punkteAusblenden && (
                  <p className="text-sm text-neutral-500">{anzeigeTreffer.kennzeichen.points} Punkte</p>
                )
              )}
              {(anzeigeTreffer.kennzeichen.derivation ?? anzeigeTreffer.kennzeichen.stateName) &&
                (() => {
                  const label = zusatzfeldLabel(anzeigeTreffer.land.iso2);
                  return (
                    // Ohne linkes Feld (Frankreich) rutscht das rechte
                    // sonst mit justify-between an den linken Rand.
                    <div className={`mt-2 flex px-2 text-sm ${label.links === null ? "justify-end" : "justify-between"}`}>
                      {label.links !== null && (
                        <div className="text-left">
                          <div className="font-medium">{label.links}</div>
                          <div>{anzeigeTreffer.kennzeichen.derivation ?? "–"}</div>
                        </div>
                      )}
                      {label.rechts !== null && (
                        <div className="text-right">
                          <div className="font-medium">{label.rechts}</div>
                          <div>{anzeigeTreffer.kennzeichen.stateName ?? "–"}</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              {treffer.length > 1 && (
                <p className="mt-1 text-xs text-neutral-400">
                  {trefferIndex + 1} / {treffer.length}
                </p>
              )}
            </>
          )}
        </div>

        {zeigePersoenliche && (
          <Ringpaar
            karte={persoenlicheKarte}
            fortschritt={fortschrittFuer(fortschrittBezug, kennzeichen, laender, persoenlicheKarte)}
            punkteAusblenden={punkteAusblenden}
          />
        )}

        {/* Der benannte Abschnitt fuer die gemeinsame Sammlung. Es gibt ihn
            in der Original-App nicht (dort gibt es keine gemeinsamen
            Sammlungen), also frei gestaltet - siehe CLAUDE.md. */}
        {gemeinsame && (
          <div className="w-full">
            {zeigePersoenliche && <div className="mb-4 h-px w-full bg-neutral-200" />}
            <p className="mb-2 text-center text-sm font-medium text-neutral-500">{gemeinsame.name}</p>
            <Ringpaar
              karte={karte}
              fortschritt={fortschritt}
              punkteAusblenden={punkteAusblenden}
            />
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Die zwei Ringe unter der Eingabe. Steht als eigene Funktion da, weil es
 * sie seit den gemeinsamen Sammlungen zweimal geben kann.
 *
 * justify-around gegen je-eine-Haelfte-zentriert getauscht (IMG_4893): die
 * Trennlinie sitzt dort exakt auf der Bildschirmmitte, nicht mittig
 * zwischen den Ringen. Einstellung "Punkte ausblenden": der Punkte-Ring UND
 * die Trennlinie entfallen, der Fortschritts-Ring nimmt die volle Breite
 * ein - kein Referenz-Screenshot fuer diesen Zustand, aber die wortnaechste
 * Lesart von "ausblenden".
 */
function Ringpaar({
  karte,
  fortschritt,
  punkteAusblenden,
}: {
  karte: Eintragskarte;
  fortschritt: Fortschritt;
  punkteAusblenden: boolean;
}) {
  return (
    <div className="flex w-full items-center">
      {!punkteAusblenden && (
        <>
          <div className="flex flex-1 justify-center">
            {/* anteil ist ein fester Platzhalter: Wonach sich der Bogen des
                Original-Rings richtet (vermutlich Rangstufen), ist nicht
                bekannt und wird auch nicht zurueckgerechnet, siehe E8 in
                docs/decisions.md fuer dieselbe Haltung bei den Punktwerten. */}
            <Ring
              anteil={0.2}
              vollton="#FDD835"
              track="#FAF5DC"
              icon={<Star size={18} fill="#FDD835" className="text-punkte" />}
              oben={String(punkteGesamt(karte))}
              unten="Punkte"
            />
          </div>
          <div className="h-16 w-px bg-neutral-300" />
        </>
      )}
      <div className="flex flex-1 justify-center">
        <Ring
          anteil={fortschritt.gesamt > 0 ? fortschritt.gesehen / fortschritt.gesamt : 0}
          vollton="#43A047"
          track="#DFECDF"
          icon={<Flag size={18} fill="#43A047" className="text-fortschritt" />}
          oben={`${fortschritt.gesehen} / ${fortschritt.gesamt}`}
          unten={fortschritt.label}
        />
      </div>
    </div>
  );
}
